import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import CRMService from "@/src/api/CRMService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
export const FIELD_TYPES = {
  NUMERIC: ["text", "number", "currency", "integer", "decimal"],
  DATE: ["date", "datetime", "date_time", "dateandtime", "date_and_time"],
  BOOLEAN: ["boolean"],
  SELECT: ["select", "dropdown", "id"],
  USER: ["user", "owner"],
  TAGS: ["tags", "multi_select"],
};

export const FILTER_CONFIG = {
  SEARCH_TIMEOUT: 150,
  DEFAULT_OPERATOR: "equals",
};

export const isNumericField = (fieldType) => {
  if (!fieldType) return false;
  return FIELD_TYPES.NUMERIC.includes(fieldType.toLowerCase());
};

export const isDateField = (fieldType) => {
  if (!fieldType) return false;
  return FIELD_TYPES.DATE.includes(fieldType.toLowerCase());
};

export const isBooleanField = (fieldType) => {
  if (!fieldType) return false;
  return fieldType.toLowerCase() === "boolean";
};

export const isSelectField = (fieldType) => {
  if (!fieldType) return false;
  return FIELD_TYPES.SELECT.includes(fieldType.toLowerCase());
};

export const isUserField = (fieldType) => {
  if (!fieldType) return false;
  return FIELD_TYPES.USER.includes(fieldType.toLowerCase());
};

export const isTagsField = (fieldType) => {
  if (!fieldType) return false;
  return FIELD_TYPES.TAGS.includes(fieldType.toLowerCase());
};

export const isSelectTimestampField = (fieldType) => {
  if (!fieldType) return false;
  return fieldType.toLowerCase() === "select_timestamp";
};

export const buildFilterFlags = (fieldType, filter = {}) => {
  const flags = {};
  const normalizedType = fieldType?.toLowerCase();

  if (isNumericField(normalizedType)) {
    flags.isTextInput = true;
    flags.customWidth = "w-80";
  }

  if (isDateField(normalizedType)) {
    flags.isTextInput = true;
    flags.isDateInput = true;
    flags.customWidth = "w-80";
  }

  if (isBooleanField(normalizedType)) {
    flags.isBooleanInput = true;
    flags.customWidth = "w-56";
    flags.options = [];
    flags.originalOptions = [];
    flags.isLoading = false;
  }

  if (isSelectField(normalizedType)) {
    flags.searchable = true;
    flags.customWidth = "w-56";
    if (filter.multiSelect) {
      flags.isMultiSelectInput = true;
    } else {
      flags.isSelectInput = true;
    }
  }

  if (isUserField(normalizedType)) {
    flags.searchable = true;
    flags.showInitials = true;
    flags.isUserInput = true;
    flags.customWidth = "w-56";
  }

  if (filter.multiSelect || isTagsField(normalizedType)) {
    flags.searchable = true;
    flags.customWidth = "w-56";
    if (!flags.isSelectInput && !flags.isUserInput) {
      flags.isMultiSelectInput = true;
    }
  }

  if (isSelectTimestampField(normalizedType)) {
    flags.isSelectTimestampInput = true;
    flags.customWidth = "w-80";
  }

  return flags;
};

export const FILTER_ID_MAPPINGS = {
  deal_owner_id: "deal_owner",
  current_company: "company",
  owner_id: "owner",
  expected_close_date: "close_date",
};

export const mapFilterId = (slug) => {
  return FILTER_ID_MAPPINGS[slug] || slug;
};

export const currentUserId = -1;
export const aiUserId = -555;

export const isSchemaEntityType = async (source) => {
  if (!source) return false;

  try {
    const schemaObjects = await CRMService.fetchDynamicCRMListObjects();
    if (schemaObjects.success) {
      return schemaObjects.objects.some(
        (obj) => obj.slug === source || obj.name === source,
      );
    }
    return false;
  } catch (error) {
    console.error("Error checking schema entity type:", error);
    return false;
  }
};

export const getFilterTypeFromDatatype = (datatype) => {
  const datatypeMap = {
    Date: "date",
    DateTime: "date",
    "Single Select": "select",
    "Multi Select": "multiselect",
    User: "user",
    Email: "text",
    "Phone Number": "text",
    URL: "text",
    "Single-line Text": "text",
    "Multi-line Text": "text",
    Number: "number",
    Currency: "currency",
    Company: "company",
    Source: "source",
  };
  return datatypeMap[datatype] || "text";
};

export const crmEntityTypes = [
  entityTypes.CONTACT,
  entityTypes.LEAD,
  entityTypes.COMPANY,
  entityTypes.DEAL,
  entityTypes.CUSTOMER,
];

// Standard object slugs (plural forms used in API routes)
export const STANDARD_OBJECT_SLUGS = [
  entityTypes.CONTACTS,
  entityTypes.LEADS,
  entityTypes.COMPANIES,
  entityTypes.DEALS,
];

export const isCRMEntityType = (source) => {
  return crmEntityTypes.includes(source);
};

export const getEntityTypeFromSource = (source) => {
  return crmEntityTypes[source] || source;
};

export const dateFilterTypes = [
  "created_date",
  "last_activity",
  "date",
  "sent_on",
  "close_date",
  "time_range",
  "due_date",
  "uploaded_on",
  "time_filter",
  "sent_time",
  "delivered_at",
  "opened_at",
  "clicked_at",
  "replied_at",
  "sent_at",
  "created_at",
  "completed_at",
  "updated_date",
];

export const datePresetOptions = [
  { id: "today", name: "Today" },
  { id: "yesterday", name: "Yesterday" },
  { id: "last_3_days", name: "Last 3 days" },
  { id: "last_7_days", name: "Last 7 days" },
  { id: "last_30_days", name: "Last 30 days" },
];

export const defaultDateFilters = [
  {
    id: "created_date",
    name: "Created Date",
    type: "date",
    datatype: "Date",
    options: datePresetOptions,
  },
  {
    id: "last_activity",
    name: "Last Activity",
    type: "date",
    datatype: "Date",
    options: datePresetOptions,
  },
];

export const systemPropertyFields = [
  "id",
  "created_by",
  "created_at",
  "updated_at",
  "last_modified_by",
];

// Fields excluded from ALL forms (including details page) - use constant IDs since slugs can be renamed
export const excludedFormFields = [
  "id",
  "_id",
  "created_at",
  "updated_at",
  "created_by",
  "updated_by",
  "workspace_id",
  "organization_id",
  "created_by_source",
  "last_activity",
  "last_modified_by",
  "project_id",
  // CRM Task internal fields
  "reminder_datetime",
  "reminder_sent",
  "sequence_execution_step_id",
  "crm_external_id",
  "completed_date",
  // QA internal relationship fields
  "suite_id",
  "test_id",
  "test_run_id",
];

export const excludedAddFormFields = ["currency_code", "deal_stage"];

export const NON_SCHEMA_PROPERTIES = [
  {
    id: "__personal_emails",
    name: "Personal Email",
    objectTypes: ["contacts", "leads"],
  },
  {
    id: "__company_phones",
    name: "Company Phone Number",
    objectTypes: ["contacts", "leads"],
  },
  {
    id: "__timezone",
    name: "Timezone",
    objectTypes: ["leads"],
  },
  {
    id: "__company_location",
    name: "Company HQ Location",
    objectTypes: ["leads"],
  },
];

// Fields excluded ONLY from create mode (shown in edit mode)
export const excludedCreateOnlyFields = ["source"];

export const excludedFilterFields = [
  "id",
  "pipeline_stage_id",
  "currency_code",
  // QA internal relationship fields
  "suite_id",
  "test_id",
  "test_run_id",
];

export const excludedFilterTypes = ["action", "image", "file"];

// Relationship mode for record-type (relation_record) attributes. Older
// attributes created before this mode existed return null / empty / a legacy
// value — normalize anything unrecognised to `lookup` so the UI always has a
// valid selection and the save path never ships an unknown value.
export const RELATIONSHIP_MODES = {
  LOOKUP: "lookup",
  MASTER_DETAIL: "master_detail",
};

export const normalizeRelationshipMode = (mode) =>
  mode === RELATIONSHIP_MODES.MASTER_DETAIL
    ? RELATIONSHIP_MODES.MASTER_DETAIL
    : RELATIONSHIP_MODES.LOOKUP;

export const EXCLUDED_DETAIL_PAGE_TYPES = ["relationship", "action", "image"];

// Attribute types that should never surface in list-style views (table
// columns, column-visibility picker, kanban card attributes list). The
// image type still drives the profile/kanban avatar via useEntityImageUrl.
export const EXCLUDED_ATTRIBUTE_TYPES_IN_LIST_VIEWS = ["image"];

// Primary attribute IDs for standard objects (used to resolve current slug from schema)
export const STANDARD_OBJECT_PRIMARY_ATTR_IDS = {
  companies: ["name"],
  contacts: ["name", "first_name"],
  leads: ["name", "first_name"],
  deals: ["title"],
};

export const STANDARD_OBJECT_FALLBACK_ATTR_IDS = {
  leads: "first_name",
  contacts: "first_name",
};

// Fields excluded from table/manage columns (by attribute id)
export const EXCLUDED_COLUMN_IDS = [
  "currency_code",
  // CRM Task internal fields
  "reminder_datetime",
  "reminder_sent",
  "sequence_execution_step_id",
  "crm_external_id",
  "completed_date",
  // QA internal relationship fields
  "suite_id",
  "test_id",
  "test_run_id",
];

// Additional fields excluded from columns for deals (by attribute id)
export const DEAL_EXCLUDED_COLUMN_IDS = ["pipeline_id", "pipeline_stage_id"];

// Fields excluded from group by options (too many options or not supported for kanban view)
export const EXCLUDED_GROUP_BY_FIELD_IDS = [
  "deal_stage",
  "pipeline_stage_id",
  "industry",
  "current_company",
];

export const EXCLUDED_ATTRIBUTE_TYPES_FOR_TASKS_TICKETS = [
  "select_timestamp",
  "formula",
  "file",
  "image",
  "action",
];

export const shouldExcludeFormField = (
  fieldName,
  fieldTypeOrId = null,
  fieldId = null,
) => {
  if (!fieldName && !fieldId) return false;

  // PRIORITY 1: Check by constant field ID first (IDs never change, slugs can be renamed)
  // This is the most reliable check since field IDs are constant
  if (fieldId) {
    const normalizedFieldId = fieldId.toLowerCase();
    const isExcludedById = excludedFormFields.some(
      (excluded) => excluded.toLowerCase() === normalizedFieldId,
    );
    if (isExcludedById) return true;

    const isExcludedFromAddFormById = excludedAddFormFields.some(
      (excluded) => excluded.toLowerCase() === normalizedFieldId,
    );
    if (isExcludedFromAddFormById) return true;
  }

  // PRIORITY 2: Check by field name/slug (fallback for backward compatibility)
  if (fieldName) {
    const normalizedFieldName = fieldName.toLowerCase();

    // Check excludedFormFields (excluded from all forms including details)
    const isExcludedByName = excludedFormFields.some(
      (excluded) => excluded.toLowerCase() === normalizedFieldName,
    );
    if (isExcludedByName) return true;

    // Check excludedAddFormFields (excluded only from Add/Create forms)
    const isExcludedFromAddForm = excludedAddFormFields.some(
      (excluded) => excluded.toLowerCase() === normalizedFieldName,
    );
    if (isExcludedFromAddForm) return true;
  }

  // PRIORITY 3: Check by field type (for formula, select_timestamp, etc.)
  if (fieldTypeOrId && typeof fieldTypeOrId === "string") {
    const normalizedType = fieldTypeOrId.toLowerCase();
    if (
      normalizedType === "formula" ||
      normalizedType === "select_timestamp" ||
      normalizedType === "action"
    ) {
      return true;
    }
  }

  return false;
};

export const defaultFilterOptions = [
  { id: 1, name: "Company", type: "text" },
  { id: 2, name: "Contact Owner", type: "text" },
  { id: 3, name: "Created Date", type: "date" },
  { id: 4, name: "Last Activity", type: "date" },
];

export const booleanOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

export const customObjectAttributeTypes = [
  { label: "Text", value: "text", icon: imageNames.PROPERTY_TYPE_TEXT },
  {
    label: "Long Text",
    value: "textarea",
    icon: imageNames.PROPERTY_TYPE_TEXT,
  },
  { label: "Number", value: "number", icon: imageNames.PROPERTY_TYPE_NUMBER },
  {
    label: "Currency",
    value: "currency",
    icon: imageNames.PROPERTY_TYPE_NUMBER,
  },
  {
    label: "Boolean",
    value: "boolean",
    icon: imageNames.PROPERTY_TYPE_BOOLEAN,
  },
  { label: "Date", value: "date", icon: imageNames.PROPERTY_TYPE_DATE },
  {
    label: "Date Time",
    value: "datetime",
    icon: imageNames.PROPERTY_TYPE_DATETIME,
  },
  {
    label: "Time",
    value: "time",
    icon: imageNames.CLOCK_GREY,
  },
  { label: "Email", value: "email", icon: imageNames.PROPERTY_TYPE_EMAIL },
  {
    label: "Phone",
    value: "phone_number",
    icon: imageNames.PROPERTY_TYPE_PHONE,
  },
  { label: "URL", value: "url", icon: imageNames.PROPERTY_TYPE_URL },
  { label: "Domain", value: "domain", icon: imageNames.DOMAIN_GREY },
  { label: "Select", value: "select", icon: imageNames.PROPERTY_TYPE_SELECT },
  {
    label: "Multi Select",
    value: "multi_select",
    icon: imageNames.PROPERTY_TYPE_MULTI_SELECT,
  },
  {
    label: "SuperAGI user",
    value: "user",
    icon: imageNames.PROPERTY_TYPE_USER,
  },
  {
    label: "File",
    value: "file",
    icon: imageNames.ATTACH_FILE_GREY,
  },
  {
    label: "Image",
    value: "image",
    icon: imageNames.IMAGE_GREY,
  },
  {
    label: "Calculate",
    value: "formula",
    icon: imageNames.CALCULATE_GREY,
  },
  {
    label: "Property Sync",
    value: "property_sync",
    icon: imageNames.COMPARE_ARROWS,
  },
  {
    label: "Record",
    value: "relation_record",
    icon: imageNames.LINK_GREY,
  },
  {
    label: "Time Since Property Change",
    value: "select_timestamp",
    icon: imageNames.CLOCK_GREY,
  },
  {
    label: "Action",
    value: "action",
    icon: imageNames.ADS_CLICK,
  },
];

export const ATTRIBUTE_TYPES = customObjectAttributeTypes.reduce(
  (acc, type) => {
    const key = type.value.toUpperCase().replace(/_/g, "_");
    acc[key] = type.value;
    return acc;
  },
  {},
);
ATTRIBUTE_TYPES.RECORD = "relation_record";
ATTRIBUTE_TYPES.TAGS = "tags";

// Attribute types that share the file upload stack (CrmFileUploadField, CrmFileTableCell).
export const FILE_ATTRIBUTE_TYPES = [
  ATTRIBUTE_TYPES.FILE,
  ATTRIBUTE_TYPES.IMAGE,
];
export const isFileLikeType = (type) =>
  FILE_ATTRIBUTE_TYPES.includes(String(type || "").toLowerCase());

// MIME types accepted for attributes of type "image". Keep in sync with IMAGE_ACCEPT_ATTR.
export const IMAGE_ALLOWED_MIME_TYPES = ["image/jpeg", "image/png"];
// `accept` attribute value for native <input type="file"> on image attributes.
export const IMAGE_ACCEPT_ATTR = "image/jpeg,image/png,.jpg,.jpeg,.png";

export const formComponentGroups = {
  textLike: ["input", "email", "url", "textarea"],
  numberLike: ["number"],
  selectLike: ["select", "multiselect", "user"],
  dateLike: ["date", "datetime", "time"],
  booleanLike: ["boolean"],
  phoneLike: ["phone_number"],
};

export const formDynamicOptionTypes = ["user", "tags"];

export const forceSelectFormFields = ["pipeline_id", "pipeline_stage_id"];

export const excludeEntityFields = [
  "id",
  "created_at",
  "updated_at",
  "currency_code",
  "deal_stage",
  "last_activity",
  // QA internal relationship fields
  "suite_id",
  "test_id",
  "test_run_id",
];

export const cardinalityInversionMap = {
  one_to_many: "many_to_one",
  many_to_one: "one_to_many",
  one_to_one: "one_to_one",
  many_to_many: "many_to_many",
};

export const associationLimitReachedTooltip =
  "Association limit reached for the selected entities";

export const FIELDS_TO_CONVERT_EMPTY_TO_NULL = [
  ATTRIBUTE_TYPES.EMAIL,
  ATTRIBUTE_TYPES.PHONE_NUMBER,
  ATTRIBUTE_TYPES.DOMAIN,
];

export const SELECT_FIELD_TYPES = [
  "select",
  "single_select",
  "multi_select",
  "user",
  "boolean",
  "tags",
];
export const DATE_FIELD_TYPES = ["date", "datetime"];

export const COPYABLE_TYPES = new Set([
  ATTRIBUTE_TYPES.TEXT,
  ATTRIBUTE_TYPES.TEXTAREA,
  ATTRIBUTE_TYPES.NUMBER,
  ATTRIBUTE_TYPES.EMAIL,
  ATTRIBUTE_TYPES.PHONE_NUMBER,
  ATTRIBUTE_TYPES.URL,
  ATTRIBUTE_TYPES.DOMAIN,
  ATTRIBUTE_TYPES.DATE,
  ATTRIBUTE_TYPES.DATETIME,
  ATTRIBUTE_TYPES.SELECT,
  ATTRIBUTE_TYPES.BOOLEAN,
  ATTRIBUTE_TYPES.FORMULA,
]);

export const linkableAttributeTypes = new Set([
  ATTRIBUTE_TYPES.URL,
  ATTRIBUTE_TYPES.DOMAIN,
]);

export const ENTITY_TAB_TITLES = {
  ACTIVITY: "Activity",
  NOTES: "Notes",
  EMAILS: "Emails",
  CALLS: "Calls",
  MEETINGS: "Meetings",
  LIVECHAT: "Livechat",
};

export const entityTabs = Object.values(ENTITY_TAB_TITLES).map((title) => ({
  title,
}));

export const ENTITY_SLUG_TO_TYPE_MAP = {
  contacts: "Contact",
  contact: "Contact",
  leads: "Lead",
  lead: "Lead",
  companies: "Company",
  company: "Company",
  deals: "Deal",
  deal: "Deal",
  meetings: "Meeting",
  meeting: "Meeting",
};

export const searchableFilterIds = [
  "company",
  "companies",
  "contact",
  "deal_company",
  "industry",
  "sequences",
  "pages_visited",
];

export const DYNAMIC_CRM_BULK_ACTION_IDS = ["edit", "delete"];

export const EXCLUDED_BULK_EDIT_PROPERTIES = [
  "phone_number",
  "email",
  "property_sync",
  "formula",
  "relation_record",
  "action",
  "image",
];

// Field names excluded from bulk edit for specific entity types
export const EXCLUDED_BULK_EDIT_FIELDS_BY_ENTITY = {
  companies: ["website"],
};

export const shouldExcludeFromBulkEdit = (
  property,
  fieldName,
  shouldExcludeFormFieldFn,
  entitySlug = null,
) => {
  if (!property) return true;

  const fieldType = (property.type || property.datatype || "").toLowerCase();

  if (shouldExcludeFormFieldFn && shouldExcludeFormFieldFn(fieldName)) {
    return true;
  }

  if (EXCLUDED_BULK_EDIT_PROPERTIES.includes(fieldType)) {
    return true;
  }

  // Check entity-specific excluded fields
  if (
    entitySlug &&
    EXCLUDED_BULK_EDIT_FIELDS_BY_ENTITY[entitySlug]?.includes(fieldName)
  ) {
    return true;
  }

  if (property.is_unique === true) {
    return true;
  }

  if (property.is_editable === false) {
    return true;
  }

  return false;
};

export const multiSelectOperationOptions = [
  {
    value: "append",
    label: "Append to current value(s)",
  },
  {
    value: "replace",
    label: "Replace current value(s)",
  },
  {
    value: "remove",
    label: "Remove from current value(s)",
  },
];

export const mapCRMTypeToDatatype = (type) => {
  const typeMap = {
    select: "select",
    single_select: "single_select",
    multi_select: "multi_select",
    text: "text",
    textarea: "textarea",
    number: "number",
    date: "date",
    datetime: "datetime",
    user: "user",
    email: "email",
    phone_number: "phone_number",
    url: "url",
    domain: "domain",
    boolean: "boolean",
    file: "file",
    formula: "formula",
    property_sync: "property_sync",
    relation_record: "relation_record",
  };
  return typeMap[type] || type;
};

export const standardObjectStatusFields = ["lead_status", "contact_status"];

export const getEntityTypeFromSlug = (slug) => {
  const standardObject = standardObjects.find((obj) => obj.slug === slug);
  return standardObject ? standardObject.viewSlug : slug;
};

export const getSourceFromSlug = (slug) => {
  const standardObject = standardObjects.find((obj) => obj.slug === slug);
  return standardObject ? `${standardObject.viewSlug}_view` : `${slug}_view`;
};

export const STANDARD_FIELDS = {
  CONTACT: [
    "email",
    "first_name",
    "last_name",
    "phone_number",
    "linkedin_url",
    "current_role",
    "current_company",
    "contact_status",
    "owner_id",
    "list_ids",
    "tags",
    "description",
  ],
  LEAD: [
    "email",
    "first_name",
    "last_name",
    "phone_number",
    "linkedin_url",
    "current_role",
    "current_company",
    "owner_id",
    "list_ids",
    "tags",
    "lead_status",
    "description",
  ],
  DEAL: [
    "title",
    "company_ids",
    "contact_ids",
    "lead_ids",
    "amount",
    "currency_code",
    "expected_close_date",
    "deal_type",
    "priority",
    "deal_owner_id",
    "pipeline_id",
    "pipeline_stage_id",
    "tags",
    "description",
    "deal_lost_reason",
  ],
  COMPANY: [
    "name",
    "industry",
    "website",
    "linkedin_url",
    "company_size",
    "lifecycle_stage",
    "annual_revenue",
    "city",
    "state",
    "country",
    "zipcode",
    "list_ids",
    "tags",
    "description",
  ],
};

export const PHONE_VALIDATION_ERROR = "Please enter a valid phone number";

export const PIPELINE_FIELD_IDS = [
  "pipeline",
  "pipeline_id",
  "deal_stage",
  "deal_stage_id",
  "pipeline_stage_id",
  "pipeline_stage",
];

export const DEFAULT_ORDERING = 999;

export const DEFAULT_ASSOCIATIONS_BY_ENTITY_TYPE = {
  contacts: ["companies", "deals"],
  leads: ["companies", "deals"],
  companies: ["contacts", "leads", "deals"],
  deals: ["contacts", "companies", "leads"],
  crm_tasks: ["contacts", "leads", "companies", "deals"],
};

// Standard object slugs that support the Lists (list_ids) field in the
// create/edit form. Used by both the customise form and the renderer.
export const LIST_IDS_SUPPORTED_SLUGS = ["contacts", "leads", "companies"];

// Standard object slugs whose POST APIs ignore relationship_id payloads, so
// custom associations can't be set on create. Used to hide the "Add
// associations" panel in the customise form for these entities.
export const ASSOCIATIONS_UNSUPPORTED_SLUGS = [
  "contacts",
  "leads",
  "companies",
];

// Built-in entity slugs whose customise form should default-select every
// available association (preserves the legacy "all on by default" UX). User-
// created custom objects are NOT in this list — for them, associations are
// opt-in to match how the rest of their schema works.
export const AUTO_SELECT_ASSOCIATIONS_SLUGS = ["deals", "crm_tasks", "invoices"];

// Special field configuration for fields requiring custom handling in details view
export const SPECIAL_FIELD_CONFIG = {
  // Company relationship fields
  current_company: {
    needsSearch: true,
    searchFn: "searchCompanies",
    displayField: "name",
    renderComponent: "company-select",
    valueType: "object", // stores full company object
  },

  // Location fields
  company_location: {
    needsOptions: true,
    optionsFn: "fetchCompanyLocations",
    renderComponent: "simple-select",
  },

  // Company-specific fields
  industry: {
    needsSearch: true,
    searchFn: "searchIndustries",
    renderComponent: "search-select",
  },
  lifecycle_stage: {
    needsOptions: true,
    optionsFn: "fetchLifecycleStageOptions",
    renderComponent: "simple-select",
  },

  // Lead source field - allows creating new sources
  source: {
    needsOptions: true,
    optionsFn: "fetchLeadSourceOptions",
    renderComponent: "creatable-select",
    allowCreate: true, // enables free-form input
  },
};

// Special fields mapping by entity type
export const SPECIAL_FIELDS_BY_ENTITY = {
  contacts: [
    "work_emails",
    "personal_emails",
    "phone_numbers",
    "company_phone_numbers",
  ],
  leads: [
    "work_emails",
    "personal_emails",
    "phone_numbers",
    "company_phone_numbers",
    "lead_status",
    "timezone",
    "company_location",
    "source",
  ],
  companies: [],
};

// Non-schema special fields that should never come from custom_properties
export const SPECIAL_NON_SCHEMA_FIELDS = [
  "phone_numbers",
  "company_phone_numbers",
  "work_emails",
  "personal_emails",
];

// Array fields that need deep cloning to prevent mutation issues
export const deepCloneArrayFields = [
  "phone_numbers",
  "company_phone_numbers",
  "work_emails",
  "personal_emails",
  "tags",
  "list_ids",
];

// Array field types that should remain as arrays
export const ARRAY_FIELD_TYPES = [
  ATTRIBUTE_TYPES.MULTI_SELECT,
  "tags",
  "file",
  "image",
];

export const isSpecialField = (fieldId) => {
  return SPECIAL_FIELD_CONFIG.hasOwnProperty(fieldId);
};

export const getSpecialFieldConfig = (fieldId) => {
  return SPECIAL_FIELD_CONFIG[fieldId];
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUUID = (str) => UUID_REGEX.test(str);

export const SERVICE_REQUIRED_ENTITY_TYPES = ["deal", "customers", "crm_tasks"];

export const ALLOWED_BULK_ASSOCIATIONS = ["crm_tasks"];

// Backend slugs for the custom CRM objects that power the Products & Invoices apps.
// Use these constants instead of hardcoded strings so a schema rename only needs one edit.
export const PRODUCTS_SLUG = "products";
export const INVOICES_SLUG = "invoices";

// HR app slugs.
export const EMPLOYEES_SLUG = "employees";
export const DEPARTMENTS_SLUG = "departments";
export const TEAMS_SLUG = "teams";
export const ROLES_SLUG = "roles";
export const ONBOARDING_SLUG = "onboarding";
export const OFFBOARDING_SLUG = "offboarding";
export const OFFICE_LOCATIONS_SLUG = "office_locations";
export const BUSINESS_UNITS_SLUG = "business_units";
export const SHIFTS_SLUG = "shifts";
export const SHIFT_MAPPINGS_SLUG = "shift_mappings";
export const HOLIDAYS_SLUG = "holidays";
export const GOALS_SLUG = "goals";
export const OKRS_SLUG = "okrs";
export const HR_SLUGS = [
  EMPLOYEES_SLUG,
  DEPARTMENTS_SLUG,
  TEAMS_SLUG,
  ROLES_SLUG,
  ONBOARDING_SLUG,
  OFFBOARDING_SLUG,
  OFFICE_LOCATIONS_SLUG,
  BUSINESS_UNITS_SLUG,
  SHIFTS_SLUG,
  SHIFT_MAPPINGS_SLUG,
  HOLIDAYS_SLUG,
  GOALS_SLUG,
  OKRS_SLUG,
];

// Backend service names — sent as ?service= on CRM API calls.
export const CRM_SERVICES = {
  CRM: "crm",
  FINANCE: "finance",
  INVOICES: "invoices",
  PRODUCTS: "products",
  QA: "qa",
  ERROR_LOGGING: "error_logging",
  HR: "hr",
};

// Slugs owned exclusively by a backend service — never disambiguate to a
// custom user object with the same slug. Always route these to the listed
// service regardless of store state.
export const SYSTEM_SLUG_SERVICE = {
  products: CRM_SERVICES.PRODUCTS,
  invoices: CRM_SERVICES.FINANCE,
  payment_terms: CRM_SERVICES.FINANCE,
  fees: CRM_SERVICES.FINANCE,
  taxes: CRM_SERVICES.FINANCE,
  discounts: CRM_SERVICES.FINANCE,
  line_items: CRM_SERVICES.INVOICES,
  crm_tasks: CRM_SERVICES.CRM,
  companies: CRM_SERVICES.CRM,
  deals: CRM_SERVICES.CRM,
  leads: CRM_SERVICES.CRM,
  contacts: CRM_SERVICES.CRM,
  customers: CRM_SERVICES.CRM,
  ...Object.fromEntries(HR_SLUGS.map((s) => [s, CRM_SERVICES.HR])),
};

/**
 * Returns the backend service name for a given object slug. Resolves in order:
 *   1. hard-coded system slug → service map (so e.g. "products" always hits
 *      the products service even if the objects store hasn't loaded yet),
 *   2. cached schema's object.service field,
 *   3. objects list (fetched on auth bootstrap),
 *   4. null (backend treats missing ?service= as "match any").
 */
export const getServiceForSlug = (slug, store) => {
  if (!slug) return null;
  if (SYSTEM_SLUG_SERVICE[slug]) return SYSTEM_SLUG_SERVICE[slug];
  if (!store) return null;
  const schema = store.getters?.["schema/getSchemaBySlug"]?.(slug);
  if (schema?.object?.service) return schema.object.service;
  const object = store.getters?.["objects/getObjectBySlugOrId"]?.(slug);
  return object?.service || null;
};

// Slugs for custom objects that should not show the "Parent" relation_record field in create/edit forms
export const NO_PARENT_FIELD_SLUGS = [PRODUCTS_SLUG, INVOICES_SLUG];

// Same intent as NO_PARENT_FIELD_SLUGS, but gated on the owning service too
// so a user-created CRM object that happens to share a slug (e.g. a CRM
// "products" alongside the products-service products) still shows the
// Parent field. null service means "applies regardless".
export const NO_PARENT_FIELD_RULES = {
  [PRODUCTS_SLUG]: CRM_SERVICES.PRODUCTS,
  [INVOICES_SLUG]: CRM_SERVICES.FINANCE,
  ...Object.fromEntries(HR_SLUGS.map((s) => [s, CRM_SERVICES.HR])),
};

export const shouldHideParentFieldForSlug = (slug, service) => {
  if (!slug) return false;
  const requiredService = NO_PARENT_FIELD_RULES[slug];
  if (!requiredService) return false;
  // If service is known, only hide when it matches the rule. When service
  // is unknown, fall back to the old slug-only behavior.
  if (!service) return true;
  return service === requiredService;
};

// Slugs whose currency fields share a single currency code across the form.
// Changing the currency on one field updates all currency fields on the record.
export const SHARED_CURRENCY_CODE_SLUGS = [PRODUCTS_SLUG];

// True when an attribute is either type="currency" or a formula/property_sync whose
// computed output_type/source_type is "currency". Used by details/table renderers.
export const isCurrencyAttribute = (attribute) => {
  if (!attribute) return false;
  const type = String(attribute.type || attribute.datatype || "").toLowerCase();
  if (type === "currency") return true;
  if (type === "formula") {
    const out = String(
      attribute.formula?.output_type || attribute.output_type || "",
    ).toLowerCase();
    return out === "currency";
  }
  if (type === "property_sync") {
    const src = String(attribute.source_type || "").toLowerCase();
    return src === "currency";
  }
  return false;
};

// Extracts { currency_value, currency_code } from any known shape the backend returns:
// - direct: { currency_code, currency_value }
// - snake-missing: { currencycode, currencyvalue }
// - formula-wrapped: { output_type: "currency", value: { currency_code, currency_value } }
// Returns null if the raw value is not a currency-shaped object.
export const readCurrencyValue = (raw) => {
  if (raw == null) return null;
  if (typeof raw !== "object") return null;

  // formula-wrapped
  if (raw.value && typeof raw.value === "object") {
    const nested = raw.value;
    if (
      nested.currency_value !== undefined ||
      nested.currency_code !== undefined ||
      nested.currencyvalue !== undefined ||
      nested.currencycode !== undefined
    ) {
      return readCurrencyValue(nested);
    }
  }

  const value = raw.currency_value ?? raw.currencyvalue;
  const code = raw.currency_code ?? raw.currencycode;
  if (value === undefined && code === undefined) return null;
  return {
    currency_value: value ?? "",
    currency_code: code ?? "",
  };
};

export const ENTITY_SINGULAR_TO_PLURAL_MAP = {
  [entityTypes.CONTACT]: entityTypes.CONTACTS,
  [entityTypes.LEAD]: entityTypes.LEADS,
  [entityTypes.COMPANY]: entityTypes.COMPANIES,
  [entityTypes.DEAL]: entityTypes.DEALS,
  [entityTypes.MEETING]: "meetings",
};

export const pluralizeEntityType = (singular) => {
  return ENTITY_SINGULAR_TO_PLURAL_MAP[singular] || singular;
};

export const ENTITY_PLURAL_TO_SINGULAR_MAP = Object.fromEntries(
  Object.entries(ENTITY_SINGULAR_TO_PLURAL_MAP).map(([singular, plural]) => [
    plural,
    singular,
  ]),
);

export const singularizeEntityType = (plural) => {
  return ENTITY_PLURAL_TO_SINGULAR_MAP[plural] || plural;
};

export const getDateTimeOptions = () => {
  const today = dayjs().startOf("day");
  const tomorrow = dayjs().add(1, "day").startOf("day");

  return [
    {
      value: null,
      label: "No Due Date",
      date: null,
    },
    {
      value: today,
      label: "Today",
      date: today,
    },
    {
      value: tomorrow,
      label: "Tomorrow",
      date: tomorrow,
    },
    {
      value: "custom",
      label: "Custom",
      date: null,
    },
  ];
};

export const specialMultiValueFields = [
  "phone_numbers",
  "company_phone_numbers",
  "work_emails",
  "personal_emails",
  "phone_number",
];

export const getTableContextMenuItems = (imageNames, handlers) => [
  {
    key: "open_new_tab",
    label: "Open in New Tab",
    icon: imageNames.GO_TO_LINK_GREY,
    onClick: handlers.onOpenInNewTab,
  },
  {
    key: "copy_link",
    label: "Copy Link",
    icon: imageNames.COPY_GREY,
    onClick: handlers.onCopyLink,
  },
  {
    key: "edit",
    label: "Edit",
    icon: imageNames.EDIT,
    onClick: handlers.onEdit,
  },
  {
    key: "delete",
    label: "Delete",
    icon: imageNames.DELETE_GREY,
    onClick: handlers.onDelete,
  },
];

const isFieldValidInSchema = (attributes, fieldSlug, value) => {
  if (!attributes || !Array.isArray(attributes)) {
    return false;
  }

  const field = attributes.find((attr) => attr.slug === fieldSlug);
  if (!field) {
    return false;
  }
  // If no value to check, just confirm field exists
  if (value === undefined) {
    return true;
  }

  // For non-select fields with a value, just return true if field exists
  return true;
};

export const getObjectDefaults = (slug, options = {}) => {
  const normalizedSlug = slug?.toLowerCase();
  const { schemaAttributes, includeDateDefaults = true } = options;

  const setIfValid = (defaults, field, value) => {
    if (isFieldValidInSchema(schemaAttributes, field, value)) {
      defaults[field] = value;
    }
  };

  switch (normalizedSlug) {
    case "crm_tasks": {
      const defaults = {};

      setIfValid(defaults, "status", "Todo");
      setIfValid(defaults, "priority", "Medium");

      if (includeDateDefaults) {
        const today = new Date();
        const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        setIfValid(defaults, "due_date", formattedDate);
        setIfValid(defaults, "due_time", "12:00 PM");
      }

      return defaults;
    }

    default:
      return {};
  }
};

export const RELATIONSHIP_MODE_OPTIONS = [
  { value: RELATIONSHIP_MODES.LOOKUP, label: "Lookup (soft reference)" },
  {
    value: RELATIONSHIP_MODES.MASTER_DETAIL,
    label: "Master–Detail (cascade delete)",
  },
];

export const LINE_ITEMS_SLUG = "line_items";

export const INVOICE_RELATED_SLUGS = [
  INVOICES_SLUG,
  LINE_ITEMS_SLUG,
  "discounts",
  "taxes",
  "fees",
  "address",
  "payment_terms",
];

export const SINGLE_SELECT_RELATION_FIELDS = {
  invoices: ["customer", "payment_terms"],
  line_items: ["product"],
};

export const isSingleSelectRelationField = (slug, propertySlug) =>
  SINGLE_SELECT_RELATION_FIELDS[slug]?.includes(propertySlug) ?? false;

// Human-readable labels for backend service identifiers. Used wherever the
// raw service name (crm, finance, invoices, …) would be shown to users.
export const SERVICE_LABELS = {
  crm: "CRM",
  finance: "Finance",
  invoices: "Invoices",
  products: "Products",
  qa: "QA",
  error_logging: "Error Logging",
  marketing: "Marketing",
  support: "Support",
};

export const formatServiceLabel = (service) => {
  if (!service) return "Other";
  if (SERVICE_LABELS[service]) return SERVICE_LABELS[service];
  return service.charAt(0).toUpperCase() + service.slice(1);
};

// Slugs whose schema is read-only only when served by their owning service.
// A CRM-scoped custom object that happens to share a slug (e.g. a CRM
// "products" alongside the products-service products) should still allow
// adding/editing columns.
export const READONLY_SCHEMA_RULES = {
  [PRODUCTS_SLUG]: CRM_SERVICES.PRODUCTS,
  [INVOICES_SLUG]: CRM_SERVICES.FINANCE,
  line_items: CRM_SERVICES.FINANCE,
  discounts: CRM_SERVICES.FINANCE,
  fees: CRM_SERVICES.FINANCE,
  taxes: CRM_SERVICES.FINANCE,
  payment_terms: CRM_SERVICES.FINANCE,
};

export const isReadonlySchema = (slug, service) => {
  if (!slug) return false;
  const requiredService = READONLY_SCHEMA_RULES[slug];
  if (!requiredService) return false;
  // If service is known, only treat as readonly when it matches the owning
  // service. When service is unknown, fall back to slug-only behavior.
  if (!service) return true;
  return service === requiredService;
};

